import { inject, Injectable } from '@angular/core';
import { BaseService } from '@csspension/base-angular';
import { TypeAPI } from '../../api/type-api';
import { map, shareReplay, tap } from 'rxjs/operators';
import { PhoneDropdownItem } from '../../models/shared/phone-dropdown-item';
import { BehaviorSubject, Observable } from 'rxjs';
import { CANADA_COUNTRY_ID, USA_COUNTRY_ID } from '../../consts/consts';
import { GeographyService } from '../geography/geography.service';

@Injectable({
  providedIn: 'root'
})
export class TypeService extends BaseService {
  private typeAPI = inject(TypeAPI);
  private geographyService = inject(GeographyService);

  constructor() {
    super();
  }

  /** @deprecated Inject {@link GeographyService} and use `statesForCountry(USA_COUNTRY_ID)`. */
  public states$ = this.geographyService.statesForCountry(USA_COUNTRY_ID);

  /** @deprecated Inject {@link GeographyService} and use `statesForCountry(CANADA_COUNTRY_ID)`. */
  public provinces$ = this.geographyService
    .statesForCountry(CANADA_COUNTRY_ID)
    .pipe(tap(() => this._provincesLoaded.next(true)));

  private _provincesLoaded = new BehaviorSubject(false);
  public provincesLoaded$ = this._provincesLoaded as Observable<boolean>;

  /** @deprecated Inject {@link GeographyService} and use `countries$`. */
  public countries$ = this.geographyService.countries$.pipe(tap(() => this._countriesAreLoaded.next(true)));
  private _countriesAreLoaded = new BehaviorSubject(false);
  public countriesAreLoaded$ = this._countriesAreLoaded as Observable<boolean>;

  // Country Codes
  public countryCodes$ = this.countries$.pipe(
    map(countries => countries.map(c => new PhoneDropdownItem(c))),
    tap(() => this._countryCodesAreLoaded.next(true)),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  private _countryCodesAreLoaded = new BehaviorSubject(false);
  public countryCodesAreLoaded$ = this._countryCodesAreLoaded as Observable<boolean>;

  // Genders
  public genders$ = this.typeAPI.getGenders().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Pronouns
  public pronouns$ = this.typeAPI.getPronouns().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Retirement Types
  public retirementTypes$ = this.typeAPI.getRetirementTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Life Status Types
  public lifeStatusTypes$ = this.typeAPI.getLifeStatusTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Death Record Contact Method Types
  public deathRecordContactMethodTypes$ = this.typeAPI
    .getDeathRecordContactMethodTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public deathRecordSettlementTypes$ = this.typeAPI.getDeathRecordSettlementTypes().pipe(shareReplay());

  // Submission Statuses
  public submissionStatuses$ = this.typeAPI
    .getSubmissionStatusTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Alert Types
  public alertTypes$ = this.typeAPI.getAlertTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Contribution Entry Option Types
  public contributionEntryOptionTypes$ = this.typeAPI
    .getContributionEntryCodeTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Transaction Entry Code Types
  public transactionEntryCodeTypes$ = this.typeAPI
    .getTransactionEntryCodeTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Transaction Status Types
  public transactionStatusTypes$ = this.typeAPI
    .getTransactionStatusTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Remittance Status Types
  public remittanceStatusTypes$ = this.typeAPI
    .getRemittanceStatusTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Remittance RemittancePayment Status Types
  public remittancePaymentStatusTypes$ = this.typeAPI
    .getRemittancePaymentStatusTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Multi-Factor Authentication Types
  public mfaTypes$ = this.typeAPI.getMfaTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Remittance Frequency Types
  public remittanceFrequencyTypes$ = this.typeAPI
    .getRemittanceFrequencyTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Designated Unit Types
  public designatedUnitTypes$ = this.typeAPI
    .getEmployerPlanDesignatedUnitTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Designated Unit Contribution Rate Basis Types
  public designatedUnitContributionRateBasisTypes$ = this.typeAPI
    .getEmployerPlanContributionRateBasisTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Designated Unit Contribution Rate Calculation Types
  public designatedUnitContributionRateCalculationTypes$ = this.typeAPI
    .getEmployerPlanContributionRateCalculationTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Designated Unit Less Than Full Time Participation Types
  public designatedUnitLessThanFullTimeParticipationTypes$ = this.typeAPI
    .getEmployerPlanLessThanFullTimeParticipationTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Member Employment Types
  public memberEmploymentTypes$ = this.typeAPI.getMemberEmploymentTypes().pipe(
    tap(() => this._memberEmploymentTypesLoaded.next(true)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private _memberEmploymentTypesLoaded = new BehaviorSubject(false);
  public memberEmploymentTypesLoaded$ = this._memberEmploymentTypesLoaded as Observable<boolean>;

  // Member Employment Status Types
  public memberEmploymentStatusTypes$ = this.typeAPI
    .getMemberEmploymentStatusTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public memberEmploymentStatusChangeReasonTypes$ = this.typeAPI
    .getMemberEmploymentStatusChangeReasonTypes()
    .pipe(shareReplay());

  public memberEmploymentStatusChangeDefinitionTypes$ = this.typeAPI
    .getMemberEmploymentStatusChangeDefinitionTypes()
    .pipe(shareReplay());

  // Member Employment Leave Types
  public memberEmploymentLeaveTypes$ = this.typeAPI
    .getMemberEmploymentLeaveTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Member Relationship Types
  public memberStatusTypes$ = this.typeAPI.getMemberStatusTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));
  public identityTypes$ = this.typeAPI.getIdentityTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public memberRelationshipTypes$ = this.typeAPI.getMemberRelationshipStatusTypes().pipe(
    tap(() => this._memberRelationshipTypesAreLoaded.next(true)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private _memberRelationshipTypesAreLoaded = new BehaviorSubject(false);
  public memberRelationshipTypesAreLoaded$ = this._memberRelationshipTypesAreLoaded as Observable<boolean>;

  // Fund Types
  public fundTypes$ = this.typeAPI.getFundTypes().pipe(shareReplay());

  // Payline Currency Types
  public paylineCurrencyTypes$ = this.typeAPI
    .getPaylineCurrencyTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: false }));

  // Payment Method Types
  public paymentMethodTypes$ = this.typeAPI
    .getPaymentMethodTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Beneficiary Relationship to Member Types
  public beneficiaryRelationshipToMemberTypes$ = this.typeAPI.getBeneficiaryRelationshipToMemberTypes().pipe(
    tap(() => this._relationshipToMemberTypesAreLoaded.next(true)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private _relationshipToMemberTypesAreLoaded = new BehaviorSubject(false);
  public relationshipToMemberTypesAreLoaded$ = this._relationshipToMemberTypesAreLoaded as Observable<boolean>;

  // Beneficiary Spousal Definition Types
  public beneficiarySpousalDefinitionTypes$ = this.typeAPI
    .getBeneficiarySpousalDefinitionTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Fiduciary Types

  public fiduciaryTypes$ = this.typeAPI.getFiduciaryTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public fiduciaryEntityTypes$ = this.typeAPI
    .getFiduciaryEntityTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public fiduciaryPowerOfAttorneyTypes$ = this.typeAPI
    .getFiduciaryPowerOfAttorneyTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public fiduciaryPowerOfAttorneyAppoinmentTypes$ = this.typeAPI
    .getFiduciaryPowerOfAttorneyAppointmentTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Employers

  public employerTypes$ = this.typeAPI.getEmployerTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public employerStatusTypes$ = this.typeAPI
    .getEmployerStatusTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public governingJurisdictionTypes$ = this.typeAPI
    .getGoverningJurisdictionTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public transferInSourceAccountTypes$ = this.typeAPI.getTransferInSourceAccountTypes().pipe(shareReplay());

  public employerVotingRegionTypes$ = this.typeAPI
    .getEmployerVotingRegionTypes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public employerPlanTypes$ = this.typeAPI.getEmployerPlanTypes().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  // Waiver Types
  public spousalWaiverTypes$ = this.typeAPI.getSpousalWaiverTypes().pipe(
    tap(() => this._spousalWaiverTypesLoaded.next(true)),
    shareReplay()
  );

  private _spousalWaiverTypesLoaded = new BehaviorSubject(false);
  public spousalWaiverTypesLoaded$ = this._spousalWaiverTypesLoaded.asObservable();

  // Monthly Pensions

  public annuityGuaranteePeriodTypes$ = this.typeAPI.getAnnuityGuaranteePeriodTypes().pipe(shareReplay());

  public annuityTypes$ = this.typeAPI.getAnnuityTypes().pipe(shareReplay());

  // Taxes

  public taxFormTypes$ = this.typeAPI.getTaxFormTypes().pipe(shareReplay());

  public taxResidencyTypes$ = this.typeAPI.getTaxResidencyTypes().pipe(shareReplay());

  public taxStatusTypes$ = this.typeAPI.getTaxStatusTypes().pipe(shareReplay());

  public taxProfileSourceTypes$ = this.typeAPI.getTaxProfileSourceTypes().pipe(shareReplay());

  public prescribedTaxZoneTypes$ = this.typeAPI.getPrescribedTaxZoneTypes().pipe(shareReplay());

  public taxWithholdingOverrideTypes$ = this.typeAPI.getTaxWithholdingOverrideTypes().pipe(shareReplay());

  public taxCraLetterOfAuthorityTypes$ = this.typeAPI.getTaxCraLetterOfAuthorityTypes().pipe(shareReplay());
}
